import React, {useEffect} from 'react';
import {useLocation, useParams} from 'react-router-dom';
import Stack from '@mui/material/Stack';
import {Box, Media, Paper, useMediaQuery} from '@tenancy.nz/ui';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import {pageView} from '../../analytics';
import CollapsibleText from '../../components/CollapsibleText';
import DeletedProperty from '../../components/DeletedProperty';
import Footer from '../../components/Footer';
import Nameplate from '../../components/Nameplate';
import IconLabel from '../../components/IconLabel';
import PageHero from '../../components/PageHero';
import PropertyAttributes from '../../components/PropertyAttributes';
import NotFound from '../../components/NotFound';
import Map from '../../components/Map';
import Section from '../../components/Section';
import SectionHeader from '../../components/SectionHeader';
import SimilarListingsFeature from './SimilarListingsFeature';
import BookingFeature from '../booking/BookingFeature';
import {useInitAgencyBySlugQuery} from '../../api/main/agency';
import {env} from '../../utils/helpers';

function ListingDetailFeature() {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('md'));
  const location = useLocation();
  const {id} = useParams();
  const {
    data: {prepared} = {prepared: {}},
    isError,
    isLoading,
  } = useInitAgencyBySlugQuery(id, {
    skip: !id,
  });
  const {agency, property} = prepared;

  useEffect(() => {
    // Log the page view in analytics
    pageView({
      path: location.pathname,
      title: document.title,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initial loading view
  if (isLoading) {
    return <Nameplate skeleton />;
  }

  // If no access, show 404 page
  if (!isLoading && (!agency.hasBookMeAccess || isError)) {
    return <NotFound />;
  }

  // Show deleted property page
  if (!isLoading && property.status === 'deleted') {
    return (
      <DeletedProperty
        url={`${env('REACT_APP_URL')}/tps${agency.clientCode}`}
      />
    );
  }

  const header = (
    <SectionHeader
      heading={property.addressPart1}
      subheading={property.addressPart2}
      mobile={isMobile}
    />
  );

  const attributesContent = (
    <Stack gap={4}>
      <IconLabel
        icon={<MonetizationOnIcon color="tertiary" />}
        size="24px"
        text={property.rent}
      />
      <PropertyAttributes
        availableAt={property.dateAvailableDisplay}
        noOfBedrooms={property.bedrooms}
        noOfBathrooms={property.bathrooms}
        noOfCarparks={property.parkingSpaces}
        iconSize="28px"
        sx={{fontSize: '14px'}}
      />
    </Stack>
  );

  const mapPopupContent = (
    <Box
      sx={{
        display: 'flex',
        gap: '15px',
        alignItems: 'flex-start',
        mt: '20px',
      }}>
      <Box width="70px" height="55px">
        <Media src={property.thumbphoto} />
      </Box>
      <Box flex="1">
        <PropertyAttributes
          address={property.address}
          noOfBedrooms={property.bedrooms}
          noOfBathrooms={property.bathrooms}
          noOfCarparks={property.parkingSpaces}
          spacing={2}
          rent={property.rent}
          sx={{gap: 2}}
        />
      </Box>
    </Box>
  );

  const content = (
    <React.Fragment>
      {!isMobile && attributesContent}
      <Box mt={2} mb={4}>
        <CollapsibleText text={property.description} />
      </Box>
      <Map
        latitude={property.latitude}
        address={property.address}
        longitude={property.longitude}
        popupContent={mapPopupContent}
        title="Location:"
      />
    </React.Fragment>
  );

  const BookingFormComponent = isMobile ? Box : Paper;
  const rightContent = (
    <React.Fragment>
      {isMobile && header}
      {isMobile && attributesContent}
      <BookingFormComponent {...(!isMobile && {inset: 'xlarge'})}>
        <BookingFeature />
      </BookingFormComponent>
    </React.Fragment>
  );

  return (
    <React.Fragment>
      <Nameplate
        skeleton={isLoading}
        heading={agency.companyName}
        logo={agency.logo}
        ctaLabel="Manage your bookings"
        ctaHref={`${env('REACT_APP_TENANT_PORTAL_URL')}/viewings`}
      />
      <PageHero fixed slides={property.images} />

      <Section
        header={!isMobile ? header : undefined}
        leftContent={content}
        rightContent={rightContent}
      />
      <SimilarListingsFeature />
      <Footer />
    </React.Fragment>
  );
}

export default ListingDetailFeature;
